import React, { useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import { programData, programMeta } from "../data/program";
import {
  Calendar,
  MapPin,
  Clock,
  Coffee,
  Utensils,
  MessageSquareText,
  Users,
} from "lucide-react";

// ---- helpers -------------------------------------------------------------
const TITLE_RE = /^(Dr\.?|Prof\.?|Dt\.?|Mr\.?|Ms\.?|Mrs\.?)\s+/i;
const initialsOf = (name = "") => {
  const clean = name.replace(TITLE_RE, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const a = parts[0][0] || "";
  const b = parts.length > 1 ? parts[parts.length - 1][0] || "" : "";
  return (a + b).toUpperCase();
};
const monoTints = [
  "from-brand-500 to-brand-700",
  "from-navy-600 to-navy-800",
  "from-brand-400 to-brand-600",
  "from-navy-500 to-navy-700",
];
const tintFor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 997;
  return monoTints[h % monoTints.length];
};

const typeStyles = {
  Symposium: "bg-brand/10 text-brand-700 ring-brand/20",
  Keynote: "bg-amber-100 text-amber-700 ring-amber-200",
  Oration: "bg-amber-100 text-amber-700 ring-amber-200",
  Plenary: "bg-indigo-100 text-indigo-700 ring-indigo-200",
  Debate: "bg-rose-100 text-rose-700 ring-rose-200",
  Panel: "bg-teal-100 text-teal-700 ring-teal-200",
  Workshop: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  Award: "bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-200",
  Papers: "bg-sky-100 text-sky-700 ring-sky-200",
  Lecture: "bg-cyan-100 text-cyan-700 ring-cyan-200",
  Ceremony: "bg-violet-100 text-violet-700 ring-violet-200",
  Quiz: "bg-lime-100 text-lime-700 ring-lime-200",
  Session: "bg-slate-100 text-slate-600 ring-slate-200",
};

const QA_RE = /^(q\s*&\s*a|q&a|panel discussion|discussion|rebuttal|closing|vote of thanks)$/i;
// Paper / poster / abstract sessions — presenters are students, show name only (no photo)
const PRES_RE = /paper presentation|oral paper|free paper|e-?poster|poster presentation/i;

// ---- circle avatar (talk speakers) --------------------------------------
const CircleAvatar = ({ name, photo, className = "w-24 h-24" }) => {
  const [err, setErr] = useState(false);
  return (
    <div className={`${className} rounded-full p-[3px] bg-gradient-to-br from-brand-300 via-brand to-navy-600 shadow-md`}>
      {photo && !err ? (
        <img
          src={photo}
          alt={name}
          loading="lazy"
          onError={() => setErr(true)}
          className="w-full h-full rounded-full object-cover ring-2 ring-white bg-slate-100"
        />
      ) : (
        <div
          className={`w-full h-full rounded-full grid place-items-center font-display font-bold text-white ring-2 ring-white text-xl bg-gradient-to-br ${tintFor(
            name
          )}`}
        >
          {initialsOf(name)}
        </div>
      )}
    </div>
  );
};

// ---- framed square avatar (chairs / moderators) -------------------------
const FramedAvatar = ({ name, photo, className = "w-[74px] h-[90px]" }) => {
  const [err, setErr] = useState(false);
  return (
    <div className="flex-shrink-0 rounded-xl bg-white p-1 ring-1 ring-slate-200 shadow-sm">
      {photo && !err ? (
        <img
          src={photo}
          alt={name}
          loading="lazy"
          onError={() => setErr(true)}
          className={`${className} rounded-lg object-cover bg-slate-100`}
        />
      ) : (
        <div
          className={`${className} rounded-lg grid place-items-center font-display font-bold text-white text-lg bg-gradient-to-br ${tintFor(
            name
          )}`}
        >
          {initialsOf(name)}
        </div>
      )}
    </div>
  );
};

// ---- chairs / moderators strip ------------------------------------------
const PeopleStrip = ({ people }) => {
  if (!people || !people.length) return null;
  const groups = [];
  people.forEach((p) => {
    let g = groups.find((x) => x.role === p.role);
    if (!g) {
      g = { role: p.role, list: [] };
      groups.push(g);
    }
    g.list.push(p);
  });
  return (
    <div className="px-4 md:px-6 py-6 bg-slate-50/70 border-b border-slate-100">
      {groups.map((g, gi) => (
        <div key={gi} className={gi ? "mt-6" : ""}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-8 bg-brand/30" />
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-navy-600">
              <Users className="w-3.5 h-3.5 text-brand" />
              {g.role}
              {g.list.length > 1 ? "s" : ""}
            </span>
            <span className="h-px w-8 bg-brand/30" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-5">
            {g.list.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <FramedAvatar name={p.name} photo={p.photo} />
                <div className="text-left leading-tight">
                  <div className="font-display font-bold text-navy-900 text-[15px]">{p.name}</div>
                  {p.city && <div className="text-[13px] text-slate-500 mt-0.5">{p.city}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---- talk card (vertical) -----------------------------------------------
const TalkCard = ({ talk }) => {
  const speakers = [
    talk.speaker && { name: talk.speaker, city: talk.city, photo: talk.photo },
    ...(talk.cospeakers || []),
  ].filter(Boolean);
  const primary = speakers[0];
  const extra = speakers.slice(1);

  return (
    <div className="group w-full sm:w-[15.5rem] flex flex-col items-center text-center rounded-2xl border border-slate-100 bg-white p-5 shadow-sm hover:shadow-md hover:border-brand/30 transition-all">
      {talk.time && (
        <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-brand to-brand-600 px-3 py-1 text-[11px] font-bold text-white shadow-sm">
          <Clock className="w-3 h-3" />
          {talk.time}
        </span>
      )}
      {primary ? (
        <CircleAvatar name={primary.name} photo={primary.photo} className="w-[92px] h-[92px]" />
      ) : (
        <div className="w-[92px] h-[92px] rounded-full grid place-items-center bg-brand/10 text-brand">
          <MessageSquareText className="w-8 h-8" />
        </div>
      )}
      {primary ? (
        <>
          <p className="mt-4 font-display font-bold text-navy-900 text-[15px] leading-tight">{primary.name}</p>
          {primary.city && <p className="mt-0.5 text-[12px] font-semibold text-brand-600">{primary.city}</p>}
          {extra.length > 0 && (
            <p className="mt-1 text-[12px] text-slate-500">
              with {extra.map((e) => e.name).join(", ")}
            </p>
          )}
        </>
      ) : null}
      {(talk.title || "").trim() ? (
        <p className={`text-[13px] leading-snug text-slate-600 ${primary ? "mt-3" : "mt-4 font-semibold text-navy-800"}`}>
          {talk.title}
        </p>
      ) : (
        <p className="mt-3 text-[12px] italic text-slate-400">Topic to be announced</p>
      )}
    </div>
  );
};

// ---- presenter row (paper/poster — students, no photo) ------------------
const PresenterRow = ({ talk }) => {
  const speakers = [
    talk.speaker && { name: talk.speaker, city: talk.city },
    ...(talk.cospeakers || []),
  ].filter(Boolean);
  return (
    <div className="w-full flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-4 rounded-xl border border-slate-100 bg-white p-4 hover:border-brand/30 transition-colors">
      {talk.time && (
        <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-brand/10 px-3 py-1 text-[11px] font-bold text-brand-700 flex-shrink-0 sm:w-[128px] justify-center">
          <Clock className="w-3 h-3" />
          {talk.time}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-navy-900 leading-snug">
          {(talk.title || "").trim() || "Topic to be announced"}
        </p>
        {speakers.length > 0 && (
          <p className="mt-1.5 text-sm">
            {speakers.map((s, i) => (
              <span key={i}>
                <span className="font-semibold text-navy-800">{s.name}</span>
                {s.city && <span className="text-brand-600"> · {s.city}</span>}
                {i < speakers.length - 1 && <span className="text-slate-300">, </span>}
              </span>
            ))}
          </p>
        )}
      </div>
    </div>
  );
};

// ---- sublabel divider ----------------------------------------------------
const SubLabel = ({ text }) => (
  <div className="w-full flex items-center gap-3 py-1">
    <span className="h-px flex-1 bg-gradient-to-r from-transparent to-brand/30" />
    <span className="text-xs font-bold uppercase tracking-wide text-brand-700 text-center">{text}</span>
    <span className="h-px flex-1 bg-gradient-to-l from-transparent to-brand/30" />
  </div>
);

// ---- talks area (flex-wrap grid, with full-width sublabels) --------------
const TalksArea = ({ talks, nameOnly }) => {
  // split into rows separated by sublabels so labels span full width
  const blocks = [];
  let cur = [];
  talks.forEach((t) => {
    if (t.sublabel) {
      if (cur.length) blocks.push({ type: "talks", items: cur }), (cur = []);
      blocks.push({ type: "label", text: t.title });
    } else {
      cur.push(t);
    }
  });
  if (cur.length) blocks.push({ type: "talks", items: cur });

  return (
    <div className="px-4 md:px-6 py-6 space-y-5">
      {blocks.map((b, i) =>
        b.type === "label" ? (
          <SubLabel key={i} text={b.text} />
        ) : nameOnly ? (
          <div key={i} className="space-y-3 max-w-3xl mx-auto">
            {b.items.map((t, j) => (
              <PresenterRow key={j} talk={t} />
            ))}
          </div>
        ) : (
          <div key={i} className="flex flex-wrap justify-center gap-4 md:gap-5">
            {b.items.map((t, j) => (
              <TalkCard key={j} talk={t} />
            ))}
          </div>
        )
      )}
    </div>
  );
};

// ---- break block ---------------------------------------------------------
const BreakBlock = ({ item }) => {
  const label = item.label.toLowerCase();
  const Icon = /lunch|dinner|breakfast/.test(label) ? Utensils : Coffee;
  return (
    <div className="my-4 flex items-center gap-3 rounded-2xl border border-dashed border-brand/30 bg-orange-50/50 px-5 py-3">
      <span className="grid place-items-center w-9 h-9 rounded-full bg-brand/10 text-brand flex-shrink-0">
        <Icon className="w-5 h-5" />
      </span>
      <span className="font-semibold text-navy-800">{item.label}</span>
      {item.time && <span className="ml-auto text-sm text-slate-500 font-mono">{item.time}</span>}
    </div>
  );
};

// ---- session card --------------------------------------------------------
const SessionCard = ({ session, index }) => {
  const badge = typeStyles[session.type] || typeStyles.Session;
  return (
    <article
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
      className="fade-in overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      {/* header */}
      <div className="relative gradient-navy px-4 md:px-6 py-5">
        <div className="absolute -top-8 -right-8 w-40 h-40 bg-brand/20 rounded-full blur-2xl" />
        <div className="relative flex flex-wrap items-center gap-x-3 gap-y-2">
          {session.time && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 backdrop-blur px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/15">
              <Clock className="w-3.5 h-3.5 text-brand-300" />
              {session.time}
            </span>
          )}
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ring-1 ${badge}`}
          >
            {session.type}
          </span>
        </div>
        {session.title && (
          <h3 className="relative mt-3 font-display font-bold text-white text-lg md:text-xl leading-snug">
            {session.title}
          </h3>
        )}
      </div>
      <PeopleStrip people={session.people} />
      {session.talks.length > 0 ? (
        <TalksArea talks={session.talks} nameOnly={PRES_RE.test(session.title || "")} />
      ) : (
        (!session.people || session.people.length === 0) && (
          <p className="px-6 py-6 text-center text-sm text-slate-400 italic">
            Session details &amp; speakers to be announced.
          </p>
        )
      )}
    </article>
  );
};

// ---- main page -----------------------------------------------------------
const Program = () => {
  const [dayIdx, setDayIdx] = useState(0);
  const day = programData[dayIdx];
  const [hallName, setHallName] = useState(day.halls[0].hall);

  const halls = day.halls;
  const activeHall = useMemo(
    () => halls.find((h) => h.hall === hallName) || halls[0],
    [halls, hallName]
  );

  const onDay = (i) => {
    setDayIdx(i);
    const h = programData[i].halls;
    if (!h.find((x) => x.hall === hallName)) setHallName(h[0].hall);
  };

  const dayMeta = [
    { n: 1, label: "Day 1", sub: "04 Sep · Fri" },
    { n: 2, label: "Day 2", sub: "05 Sep · Sat" },
    { n: 3, label: "Day 3", sub: "06 Sep · Sun" },
  ];

  return (
    <div>
      <PageHeader
        title="Scientific Agenda"
        crumb="Program"
        subtitle="The full three-day programme across every hall — symposia, orations, debates, workshops and free papers. Choose a day, then a hall."
      />

      {/* meta chips */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-5 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-navy-800">
            <Calendar className="w-4 h-4 text-brand" /> {programMeta.dates}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-navy-800">
            <MapPin className="w-4 h-4 text-brand" /> {programMeta.venue}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1.5 text-sm font-medium text-navy-800">
            <Users className="w-4 h-4 text-brand" /> {programMeta.faculty}+ Faculty
          </span>
        </div>
      </section>

      <section className="section-pad bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          {/* DAY SELECTOR */}
          <div className="grid grid-cols-3 gap-2.5 md:gap-4">
            {programData.map((d, i) => {
              const active = i === dayIdx;
              const m = dayMeta[i];
              return (
                <button
                  key={d.day}
                  onClick={() => onDay(i)}
                  className={`group flex items-center gap-2.5 rounded-2xl border px-3 md:px-5 py-2.5 text-left transition-all ${
                    active
                      ? "border-transparent bg-brand text-white shadow-lg shadow-brand/25"
                      : "border-slate-200 bg-white text-navy-900 hover:border-brand/40 hover:shadow-sm"
                  }`}
                >
                  <span
                    className={`grid place-items-center w-8 h-8 rounded-xl font-display font-bold text-sm flex-shrink-0 ${
                      active ? "bg-white/20 text-white" : "bg-brand/10 text-brand"
                    }`}
                  >
                    {m.n}
                  </span>
                  <span className="leading-tight min-w-0">
                    <span className="block font-display font-bold text-sm">{m.label}</span>
                    <span
                      className={`block text-[10px] md:text-[11px] font-semibold whitespace-nowrap ${
                        active ? "text-white/80" : "text-slate-500"
                      }`}
                    >
                      {m.sub}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* HALL SELECTOR */}
          <div className="mt-4 flex flex-wrap gap-2.5">
            {halls.map((h) => {
              const active = h.hall === activeHall.hall;
              return (
                <button
                  key={h.hall}
                  onClick={() => setHallName(h.hall)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                    active
                      ? "bg-navy-800 text-white shadow-md"
                      : "bg-white text-navy-700 border border-slate-200 hover:border-navy-300"
                  }`}
                >
                  <MapPin className={`w-4 h-4 ${active ? "text-brand-300" : "text-brand"}`} />
                  {h.hall.replace(/ HALL$/i, "")}
                  <span
                    className={`ml-0.5 grid place-items-center min-w-[20px] h-5 px-1 rounded-full text-[11px] font-bold ${
                      active ? "bg-brand text-white" : "bg-slate-100 text-navy-600"
                    }`}
                  >
                    {h.sessionCount}
                  </span>
                </button>
              );
            })}
          </div>

          {/* HALL HEADER */}
          <div className="mt-8 flex items-center gap-3">
            <span className="h-9 w-1.5 rounded-full bg-brand" />
            <div>
              <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy-900">
                {activeHall.hall}
              </h2>
              <p className="text-sm text-slate-500">
                {day.day} · {day.date}
              </p>
            </div>
          </div>

          {/* SESSIONS */}
          <div key={`${dayIdx}-${activeHall.hall}`} className="mt-6 space-y-5">
            {activeHall.items
              .filter(
                (item) =>
                  !(item.kind === "session" && /^all speakers?$/i.test((item.title || "").trim()))
              )
              .map((item, i) =>
                item.kind === "break" ? (
                  <BreakBlock key={i} item={item} />
                ) : (
                  <SessionCard key={i} session={item} index={i} />
                )
              )}
          </div>

          <p className="mt-10 text-center text-sm text-slate-400">
            Programme subject to change. Timings are indicative and may be updated closer to the event.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Program;
